import {
  BaseQueryFn,
  FetchArgs,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import {
  acheteur,
  activites,
  bannieres,
  baseUrl,
  code,
  collection,
  divisions,
  ensemble,
  fournisseur,
  login,
  magasin,
  magasins,
  modele,
  planogramme,
  planogramme_produit,
  planogrammes,
  produit,
  produit_section,
  secCombo,
  secMagasin,
  sections,
  selection,
  stats,
  statut,
  users,
  vise,
} from "../../constante/routes";
import { Section } from "../../models/Section";
import { RootState } from "../store";
import {
  addLoadingRequest,
  logout,
  removeLoadingRequest,
  setd_inv01rp,
  setd_inv026,
  setd_inv170,
} from "./appStateSlice";
import { Modele } from "../../models/Modele";
import { Produit } from "../../models/Produit";
import { Fournisseur } from "../../models/Fournisseur";
import { Division } from "../../models/Division";
import { Magasin } from "../../models/Magasin";
import { Banniere } from "../../models/Banniere";
import { Activite } from "../../models/Activite";
import { User } from "../../models/User";
import { Footer } from "../../models/Footer";
import { SecMagasin } from "../../models/SecMagasin";
import { navigation } from "../../constante/navigation";
import { Planogramme } from "../../models/Planogramme";
import { Acheteur } from "../../models/Acheteur";
import { GroupProduit } from "../../models/GroupProduit";
import { Collection } from "../../models/Collection";
import { Statut } from "../../models/Statut";
import { APIError } from "../../errors/APIError";
import { addTreeViewSettings } from "../../commons/helpers/TreeViewHelper";
import { PlanogrammeProduit } from "../../models/PlanogrammeProduit";

const baseQuery = fetchBaseQuery({
  baseUrl: baseUrl,
  prepareHeaders: (headers: Headers, { getState }): Headers => {
    return setHeaders(headers, (getState() as RootState).appState.token);
  },
}) as unknown as BaseQueryFn<string | FetchArgs, unknown, APIError, {}>;
const baseQueryInterceptor: BaseQueryFn<
  string | FetchArgs,
  unknown,
  APIError
> = async (args, api, extraOptions) => {
  api.dispatch(addLoadingRequest(args));
  let result = await baseQuery(args, api, extraOptions);
  api.dispatch(removeLoadingRequest(args));

  if (result.error) {
    switch (result.error.status) {
      case 401:
      case 403:
        api.dispatch(logout());
        break;
      case 400:
      case 500:
        navigation.navigate!("/error", { state: result.error.data });
        break;
    }
  }
  return result;
};

export const planoAPI = createApi({
  reducerPath: "apiSlice",
  tagTypes: [
    "sections",
    "planogramme",
    "plano",
    "magasin",
    "selections",
    "sectionMagasin",
    "collections",
    "planogrammeProduits",
    "bannieres",
    "divisions",
    "fournisseurs",
    "statuts",
    "activites",
  ],
  baseQuery: baseQueryInterceptor,
  endpoints: (builder) => ({
    getSections: builder.query<
      Section[],
      {
        div: string;
        simple: boolean;
        user: string;
        rech: string;
        treeView: boolean;
      }
    >({
      query: ({ div, simple, user, rech }) =>
        `${sections}${getUrlParams({ div, code, simple, user, rech })}`,
      transformResponse: (apiResponse: Section[], _meta: any, args: any) => {
        if (args.treeView) return addTreeViewSettings(apiResponse, "sections");

        return apiResponse;
      },
      providesTags: ["sections"],
    }),
    getPlanogramme: builder.query<
      GroupProduit[],
      {
        first: string | undefined;
        mag: string;
        banniere: string;
        section: string;
        four: string;
        div: string;
        page: number;
        rech: string;
        limite: number;
        acht: string;
        act: string;
        toSort: string;
        order: string;
        statut: string;
        mode: string;
        coll: string;
      }
    >({
      query: ({
        first,
        mag,
        banniere,
        section,
        four,
        div,
        page,
        rech,
        limite,
        acht,
        act,
        toSort,
        order,
        statut,
        mode,
        coll,
      }) => {
        let Tri: string | undefined = "";
        if (first?.includes("plans_nom"))
          first = first?.replace(/plans_nom/g, "plans_ordre,plans_nom");

        first?.includes("div_nom")
          ? (Tri = first?.replace("div_nom", "div_num"))
          : (Tri = first);

        return `${planogrammes}${getUrlParams({
          limite,
          page,
          section,
          four,
          div,
          banniere,
          Tri,
          mag,
          statut,
          code,
          rech,
          acht,
          act,
          toSort,
          order,
          mode,
          coll,
        })}`;
      },
      providesTags: ["planogramme"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return endpointName + getUrlParams({ ...queryArgs, page: 0 });
      },
      merge: (currentCacheData, responseData, args) => {
        if (args.arg.page > 1) {
          currentCacheData = currentCacheData.concat(responseData);
          return currentCacheData;
        }
        return responseData;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
    }),
    getModeles: builder.query<Modele[], { four: string; withProd: string }>({
      query: ({ four, withProd }) =>
        `${modele}${getUrlParams({ four, withProd, code })}`,
    }),
    getCollections: builder.query<
      Collection[],
      { four: string; mod: string; treeView: boolean }
    >({
      query: ({ four, mod, treeView }) =>
        `${collection}${getUrlParams({ four, mod, treeView, code })}`,
      transformResponse: (apiResponse: Collection[], _meta: any, args: any) => {
        if (args.treeView)
          return addTreeViewSettings(apiResponse, "collections");

        return apiResponse;
      },
      providesTags: ["collections"],
    }),
    getSelections: builder.query<Selection[], { section: number }>({
      query: ({ section }) => `${selection}${getUrlParams({ section, code })}`,
      providesTags: ["selections"],
    }),
    getDefaultSection: builder.query<Section[], { ptr: string; arg: string }>({
      query: ({ ptr, arg }) =>
        `${produit_section}${getUrlParams({ ptr, arg, code })}`,
    }),
    getPlano: builder.query<
      Planogramme[],
      {
        prod: string;
        statut: string;
        ban: string;
        mag: string;
        manquant: boolean;
      }
    >({
      query: ({ prod, statut, ban, mag, manquant }) =>
        `${planogramme}${getUrlParams({
          prod,
          code,
          statut,
          ban,
          mag,
          manquant,
        })}`,
      providesTags: ["plano"],
      transformResponse: (response: Planogramme[]) => {
        response.forEach((e) => (e.isChecked = false));
        return response;
      },
    }),
    getStatsCollection: builder.query<Produit[], { prod: string }>({
      query: ({ prod }) => `${stats}${getUrlParams({ prod, code })}`,
      providesTags: ["plano"],
    }),
    loginWithToken: builder.query<any, string>({
      queryFn: async (token, queryApi, _extraOptions, fetchWithBaseQuery) => {
        try {
          const result = await fetchWithBaseQuery(
            `${login}${getUrlParams({ token, code })}`
          );
          if (result.data) {
            const authorizations = (result.data as any).authorization;
            const d_inv01rp = authorizations.find(
              (a: any) => a.functName === "d_inv01rp"
            )
              ? authorizations.find((a: any) => a.functName === "d_inv01rp")
              : {};
            queryApi.dispatch(setd_inv01rp(d_inv01rp));
            const d_inv026 = authorizations.find(
              (a: any) => a.functName === "d_inv026"
            )
              ? authorizations.find((a: any) => a.functName === "d_inv026")
              : {};
            queryApi.dispatch(setd_inv026(d_inv026));
            const d_inv170 = authorizations.find(
              (a: any) => a.functName === "d_inv170"
            )
              ? authorizations.find((a: any) => a.functName === "d_inv170")
              : {};
            queryApi.dispatch(setd_inv170(d_inv170));
          }
          return result;
        } catch (error: any) {
          return { error };
        }
      },
    }),
    getProduits: builder.query<
      Produit[],
      { Four: string; Mod: string; Coll: string }
    >({
      query: ({ Four, Mod, Coll }) =>
        `${produit}${getUrlParams({ code, Four, Mod, Coll })}`,
    }),
    getSecCombo: builder.query<Produit[], void>({
      query: () => `${secCombo}${getUrlParams({ code })}`,
    }),
    getQueries: builder.query<
      any[],
      { rech: string; queries: string; treeView: boolean }
    >({
      query: ({ rech, queries, treeView }) =>
        `${queries.toLowerCase()}${getUrlParams({ rech, code, treeView })}`,
    }),
    getFournisseurs: builder.query<
      Fournisseur[],
      { rech: string; withMod: string; treeView: boolean }
    >({
      query: ({ rech, withMod, treeView }) =>
        `${fournisseur}${getUrlParams({ rech, withMod, treeView, code })}`,
      transformResponse: (
        apiResponse: Fournisseur[],
        _meta: any,
        args: any
      ) => {
        if (args.treeView) {
          return addTreeViewSettings(apiResponse, "fournisseurs");
        }

        return apiResponse;
      },
      providesTags: ["fournisseurs"],
    }),
    getOneFournisseurs: builder.query<Fournisseur, { four: string }>({
      query: ({ four }) => `${fournisseur}/one${getUrlParams({ four, code })}`,
    }),
    getDivisions: builder.query<
      Division[],
      {
        treeView: boolean;
        includeSections: boolean;
        plano: boolean;
        user?: string;
      }
    >({
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        const result = await baseQuery(
          `${divisions}${getUrlParams({
            code: code,
            plano: arg.plano,
            user: arg.user,
          })}`
        );
        if (result.error) return { error: result.error };

        if (arg.treeView) {
          const treeViewData = addTreeViewSettings(
            result.data as Division[],
            arg.includeSections ? "divisions" : "onlyDivisions"
          );
          if (arg.includeSections) {
            const resultSections = await baseQuery(
              `${sections}${getUrlParams({
                code,
                simple: true,
                user: arg.user,
              })}`
            );
            treeViewData.forEach((b) => {
              const children = addTreeViewSettings(
                resultSections.data as Section[],
                "sections"
              ).filter((m) => m.div_num === b.div_num);
              b.children = children;
              if (!children || children.length === 0) b.hasChildren = false;
            });
          }
          return { data: treeViewData };
        }

        return { data: result.data as Division[] };
      },
      providesTags: ["divisions"],
    }),
    getStatut: builder.query<Statut[], boolean>({
      query: () => `${statut}${getUrlParams({ code })}`,
      transformResponse: (apiResponse: Statut[], _meta: any, args: any) => {
        if (args) return addTreeViewSettings(apiResponse, "statuts");

        return apiResponse;
      },
      providesTags: ["statuts"],
    }),
    getMagasins: builder.query<
      Magasin[],
      { banniere: string; treeView: boolean }
    >({
      query: ({ banniere }) => `${magasins}${getUrlParams({ banniere, code })}`,
      transformResponse: (response: Magasin[], _meta: any, args: any) => {
        if (args.treeView) {
          return addTreeViewSettings(response, "magasins");
        }

        return response;
      },
    }),
    getBannieres: builder.query<
      Banniere[],
      { InclMag: boolean; treeView: boolean }
    >({
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        const result = await baseQuery(
          `${bannieres}${getUrlParams({ code: code, InclMag: arg.InclMag })}`
        );
        if (result.error) return { error: result.error };

        if (arg.treeView) {
          const treeViewData = addTreeViewSettings(
            result.data as Banniere[],
            "bannieres"
          );
          const resultMagasins = await baseQuery(
            `${magasins}${getUrlParams({ code })}`
          );
          treeViewData.forEach(
            (b) =>
              (b.children = addTreeViewSettings(
                resultMagasins.data as Magasin[],
                "magasins"
              ).filter((m) => m.maggrp_cd === b.maggrp_cd))
          );
          return { data: treeViewData.reverse() };
        }

        return { data: (result.data as Banniere[]).reverse() };
      },
      providesTags: ["bannieres"],
    }),
    getActivites: builder.query<Activite[], boolean>({
      query: () => `${activites}${getUrlParams({ code })}`,
      transformResponse: (apiResponse: Activite[], _meta: any, args: any) => {
        if (args) return addTreeViewSettings(apiResponse, "activites");

        return apiResponse;
      },
      providesTags: ["activites"],
    }),
    getUsers: builder.query<
      User[],
      { fonction: string; sort: string; div_num: string }
    >({
      query: ({ fonction, sort, div_num }) =>
        `${users}${getUrlParams({ fonction, code, sort, div_num })}`,
      transformResponse: (acheteurs: any[]) => {
        acheteurs.map((a) => (a.text = a.usr_nom));
        return acheteurs;
      },
    }),
    getFooter: builder.query<
      Footer,
      {
        plan: number;
        first: string | undefined;
        mag: string;
        banniere: string;
        section: string;
        four: string;
        div: string;
        page: number;
        rech: string;
        limite: number;
        acht: string;
        act: string;
        toSort: string;
        order: string;
        statut: string;
      }
    >({
      query: ({
        plan,
        first,
        mag,
        banniere,
        section,
        four,
        div,
        page,
        rech,
        limite,
        acht,
        act,
        toSort,
        order,
        statut,
      }) =>
        `${vise}${getUrlParams({
          code,
          plan,
          first,
          mag,
          banniere,
          section,
          four,
          div,
          page,
          rech,
          limite,
          acht,
          act,
          toSort,
          order,
          statut,
        })}`,
    }),
    getSecMagasins: builder.query<
      SecMagasin[],
      { sec: number | undefined; mag: string }
    >({
      query: ({ sec, mag }) =>
        `${secMagasin}${getUrlParams({ code, sec, mag })}`,
      providesTags: ["magasin"],
    }),
    getPlanogrammeProduits: builder.query<
      PlanogrammeProduit[],
      string | number
    >({
      query: (planoSeq) =>
        `${planogramme_produit}${getUrlParams({ code, planoSeq })}`,
      providesTags: ["planogrammeProduits"],
    }),
    getEnsemble: builder.query<Produit[], string | number>({
      query: (prodNum) => `${ensemble}${getUrlParams({ code, prodNum })}`,
    }),
    updateProduit: builder.mutation<any, { body: any; question: string }>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `${produit}?code=${code}&${arg.question}`,
          method: "PUT",
          body: arg.body,
        });
        if (result.error) return { error: result.error };

        return { data: result.data as any };
      },
      invalidatesTags: ["plano"],
    }),
    updateSection: builder.mutation({
      query: ({ body }) => ({
        url: `${sections}${getUrlParams({ code })}`,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["sections"],
    }),
    updatePlanogramme: builder.mutation<any, { body: any; question: string }>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `${planogramme}?code=${code}&${arg.question}`,
          method: "PUT",
          body: arg.body,
        });
        if (result.error) return { error: result.error };

        return { data: result.data as any };
      },
      invalidatesTags: ["plano"],
    }),
    UpdateGroupProduit: builder.mutation({
      query: ({ body }) => ({
        url: `${planogramme}/section${getUrlParams({ code })}`,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["plano", "planogramme"],
    }),
    addPlanogramme: builder.mutation<
      Planogramme,
      { body: any; question: string }
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `${planogramme}?code=${code}&${arg.question}`,
          method: "POST",
          body: arg.body,
        });
        if (result.error) return { error: result.error };

        return { data: result.data as any };
      },
    }),
    updateSelection: builder.mutation({
      query: ({ body }) => ({
        url: `${selection}${getUrlParams({ code })}`,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["selections", "sections"],
    }),
    updateMagasin: builder.mutation({
      query: ({ body }) => ({
        url: `${magasin}${getUrlParams({ code })}`,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["sections", "magasin"],
    }),
    updatePlanogrammeProduits: builder.mutation<
      any,
      { body: any; planoSeq: string | number; question: string }
    >({
      query: ({ body, planoSeq, question }) => ({
        url: `${planogramme_produit}${getUrlParams({
          code,
          planoSeq,
        })}&${question}`,
        method: "PUT",
        body: body,
      }),
    }),
    deleteSelection: builder.mutation({
      query: ({ body }) => ({
        url: `${selection}${getUrlParams({ code })}`,
        method: "DELETE",
        body: body,
      }),
      invalidatesTags: ["sections", "selections"],
    }),
    deletePlanogramme: builder.mutation<any, { body: any; question: string }>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `${planogramme}?code=${code}&${arg.question}`,
          method: "DELETE",
          body: arg.body,
        });
        if (result.error) return { error: result.error };

        return { data: result.data as any };
      },
      invalidatesTags: ["plano", "planogramme"],
    }),
    deleteMagasin: builder.mutation({
      query: ({ body }) => ({
        url: `${magasin}${getUrlParams({ code })}`,
        method: "DELETE",
        body: body,
      }),
      invalidatesTags: ["sections", "magasin"],
    }),
    deleteSection: builder.mutation({
      query: ({ body }) => ({
        url: `${sections}${getUrlParams({ code })}`,
        method: "DELETE",
        body: body,
      }),
      invalidatesTags: ["sections"],
    }),
    deleteAcheteur: builder.mutation({
      query: ({ body }) => ({
        url: `${acheteur}${getUrlParams({ code })}`,
        method: "DELETE",
        body: body,
      }),
      invalidatesTags: ["sections"],
    }),
    addSection: builder.mutation<Section, any>({
      query: ({ body }) => ({
        url: `${sections}${getUrlParams({ code })}`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["sections"],
    }),
    addSecMagasin: builder.mutation<SecMagasin[], any>({
      query: ({ body }) => ({
        url: `${magasin}${getUrlParams({ code })}`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["sections", "magasin"],
    }),
    addSelection: builder.mutation<Selection[], any>({
      query: ({ body }) => ({
        url: `${selection}${getUrlParams({ code })}`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["sections", "selections"],
    }),
    addAcheteur: builder.mutation<Acheteur[], any>({
      query: ({ body }) => ({
        url: `${acheteur}${getUrlParams({ code })}`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["sections"],
    }),
    login: builder.mutation<any, any>({
      queryFn: async (
        body: any,
        queryApi,
        _extraOptions,
        fetchWithBaseQuery
      ) => {
        try {
          const result = await fetchWithBaseQuery({
            url: `${login}${getUrlParams({ code })}`,
            method: "POST",
            body: body,
          });
          if (result.data) {
            const authorizations = (result.data as any).authorization;
            const d_inv01rp = authorizations.find(
              (a: any) => a.functName === "d_inv01rp"
            )
              ? authorizations.find((a: any) => a.functName === "d_inv01rp")
              : {};
            queryApi.dispatch(setd_inv01rp(d_inv01rp));
            const d_inv026 = authorizations.find(
              (a: any) => a.functName === "d_inv026"
            )
              ? authorizations.find((a: any) => a.functName === "d_inv026")
              : {};
            queryApi.dispatch(setd_inv026(d_inv026));
            const d_inv170 = authorizations.find(
              (a: any) => a.functName === "d_inv170"
            )
              ? authorizations.find((a: any) => a.functName === "d_inv170")
              : {};
            queryApi.dispatch(setd_inv170(d_inv170));
          }
          return result;
        } catch (error: any) {
          return { error };
        }
      },
    }),
  }),
});

function setHeaders(headers: Headers, token: string): Headers {
  headers.set("Content-Type", "application/json");
  headers.set("Authorization", token);
  return headers;
}

function getUrlParams(args: any) {
  let parameters = "";
  let operateur = "?";
  Object.keys(args).forEach((arg) => {
    if (
      (args[arg] || args[arg] === 0 || args[arg] === false) &&
      args[arg] !== -1
    ) {
      parameters = parameters.concat(`${operateur}${arg}=${args[arg]}`);
      operateur = operateur === "?" ? "&" : operateur;
    }
  });

  return parameters;
}

export const {
  useLazyGetFooterQuery,
  useLazyGetOneFournisseursQuery,
  useGetSectionsQuery,
  useGetSelectionsQuery,
  useGetPlanoQuery,
  useGetDefaultSectionQuery,
  useLazyGetDefaultSectionQuery,
  useGetStatsCollectionQuery,
  useGetPlanogrammeQuery,
  useLazyLoginWithTokenQuery,
  useGetStatutQuery,
  useGetModelesQuery,
  useGetCollectionsQuery,
  useUpdateProduitMutation,
  useGetProduitsQuery,
  useGetSecComboQuery,
  useUpdateGroupProduitMutation,
  useGetQueriesQuery,
  useLazyGetQueriesQuery,
  useGetFournisseursQuery,
  useGetDivisionsQuery,
  useGetMagasinsQuery,
  useGetBannieresQuery,
  useGetActivitesQuery,
  useGetUsersQuery,
  useGetFooterQuery,
  useGetSecMagasinsQuery,
  useGetPlanogrammeProduitsQuery,
  useGetEnsembleQuery,
  useUpdateSectionMutation,
  useUpdatePlanogrammeMutation,
  useUpdateSelectionMutation,
  useUpdateMagasinMutation,
  useUpdatePlanogrammeProduitsMutation,
  useDeleteSelectionMutation,
  useDeleteAcheteurMutation,
  useDeletePlanogrammeMutation,
  useDeleteMagasinMutation,
  useDeleteSectionMutation,
  useAddSectionMutation,
  useAddAcheteurMutation,
  useAddSecMagasinMutation,
  useAddSelectionMutation,
  useLoginMutation,
  useAddPlanogrammeMutation,
  useLazyGetBannieresQuery,
  useLazyGetPlanoQuery,
  useLazyGetMagasinsQuery,
  useLazyGetSectionsQuery,
} = planoAPI;
